
import { faBan, faDownload, faEdit, faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import IconButton from 'components/button/IconButton';
import { useLocale } from 'hooks';
import { dateTimeToString, dateToString } from 'lib/utils';
import { useI18n } from 'locales';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useCallback, useRef } from 'react';
import styles from './Table.module.scss';
import TableHead from './TableHead';
import TableToolbar from './TableToolbar';

const rowsPerPage = 20;

function ResponsiveTable({
  ref,
  height,
  modelName,
  params,
  info,
  filtersInfo,
  headCells,
  hideToolbar,
  multiple,
  renderRow,
  renderMobileCard,
  onCreate,
  showSelect,
  onClickView,
  onClickEdit,
  onClickDelete,
  onClickUnsubscribe,
}) {
  const tableToolbarRef = useRef();
  const tablePaginationRef = useRef();
  const dataRows = get(info, 'data.items', []);
  const { current_page, items_per_page, total_item_count } = get(info, 'data', {});
  const isFetching = info.isFetching;
  const t = useI18n();
  const locale = useLocale();

  const handleRequestSort = useCallback((event, property) => {
    const isAsc = params.getSort() === property && params.getOrder() === 'asc';
    params.setSort(property);
    params.setOrder(isAsc ? 'desc' : 'asc');
  }, [params]);

  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      const selected = params.getSelected();
      let nextSelected = selected.union(dataRows.map((n) => n.id));
      if (nextSelected.size === selected.size) {
        nextSelected = selected.filter(id => !dataRows.map(n => n.id).includes(id));
      }
      params.setSelected(nextSelected);
    } else {
      let selected = params.getSelected();
      selected = selected.clear();
      params.setSelected(selected);
    }
  }, [params, dataRows]);

  const handleClick = useCallback((event, id) => {
    const selected = params.getSelected();
    if (selected.has(id)) {
      params.setSelected(selected.remove(id));
    } else {
      params.setSelected(multiple ? selected.add(id) : selected.clear().add(id));
    }
  }, [params, multiple]);

  const handleChangePage = useCallback((event, newPage) => {
    params.setPage(newPage + 1);
  }, [params]);

  const handleChangeFilter = useCallback((name, value) => {
    params.setFilters(params.getFilters().set(name, value));
  }, [params]);

  // Default mobile card renderer if not provided
  const defaultRenderMobileCard = useCallback((row, index) => {
    const dataCells = headCells.filter(cell => cell.type !== 'actions');
    const actionsCell = headCells.find(cell => cell.type === 'actions');
    const renderActionButton = (action, actionIndex) => {
      switch (action) {
        case 'view':
          return (
            <IconButton
              key={`action-${actionIndex}`}
              icon={faEye}
              tooltip='View'
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                onClickView && onClickView(row);
              }}
            />
          );
        case 'edit':
          return onClickEdit && (
            <IconButton
              key={`action-${actionIndex}`}
              icon={faEdit}
              tooltip='Edit'
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(row);
              }}
            />
          );
        case 'delete':
          return onClickDelete && (
            <IconButton
              key={`action-${actionIndex}`}
              icon={faTrashAlt}
              tooltip='Delete'
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                onClickDelete(row);
              }}
            />
          );
        case 'download':
          return (
            <IconButton
              key={`action-${actionIndex}`}
              icon={faDownload}
              tooltip='Download'
              size='small'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                onClickView && onClickView(row);
              }}
            />
          );
        case 'unsubscribe':
          return (
            row.status === 'Enabled' &&
            <IconButton
              key={`action-${index}`}
              icon={faBan}
              tooltip='Unsubscribe'
              size='small'
              color='error'
              onClick={(e) => {
                e.stopPropagation();
                onClickUnsubscribe && onClickUnsubscribe(row);
              }}
            />
          );
        default:
          return null;
      }
    };

    return (
      <Card key={`mobile-card-${index}`} sx={{ boxShadow: 2 }}>
        <CardContent>
          <Stack gap={1.5}>
            {dataCells.map((cell, cellIndex) => {
              let value;

              if (cell.render) {
                value = cell.render({ row, locale, dispatch: null });
              } else if (cell.type === 'datetime') {
                value = get(row, cell.id) ? dateTimeToString(get(row, cell.id)) : '-';
              } else if (cell.type === 'date') {
                value = get(row, cell.id) ? dateToString(get(row, cell.id)) : '-';
              } else {
                value = get(row, cell.id, '-');
              }

              return (
                <Box key={`cell-${cellIndex}`}>
                  {cellIndex > 0 && <Divider sx={{ mb: 1.5 }} />}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                      {t(cell.label)}
                    </Typography>
                    <Box sx={{ textAlign: 'right', maxWidth: '60%' }}>
                      {typeof value === 'string' || typeof value === 'number' ? (
                        <Typography variant='body2' color='info.700'>
                          {value}
                        </Typography>
                      ) : (
                        value
                      )}
                    </Box>
                  </Stack>
                </Box>
              );
            })}

            {actionsCell && actionsCell.actions && (
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                    {t(actionsCell.label)}
                  </Typography>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                    {actionsCell.actions.map(renderActionButton)}
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }, [headCells, onClickView, onClickEdit, onClickDelete, onClickUnsubscribe, locale, t]);

  const mobileCardRenderer = renderMobileCard || defaultRenderMobileCard;

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      {/* Mobile Card Layout */}
      <Stack gap={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {isFetching ? (
          <Skeleton variant="rectangular" width="100%" height={200} />
        ) : dataRows.length > 0 ? (
          dataRows.map((row, index) => mobileCardRenderer(row, index))
        ) : (
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant='body2' color='info.700' sx={{ textAlign: 'center', py: 2 }}>
                No records found
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Mobile Pagination */}
        {dataRows.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[items_per_page]}
            component="div"
            count={total_item_count}
            rowsPerPage={rowsPerPage}
            page={current_page - 1}
            onPageChange={handleChangePage}
            showFirstButton
            showLastButton
            sx={{
              '.MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: 'center',
              },
            }}
          />
        )}
      </Stack>

      {/* Desktop Table Layout */}
      <Paper sx={{ width: '100%', display: { xs: 'none', md: 'block' } }}>
        {
          !hideToolbar && (
            <>
              <div className={styles.topSpace} />
              <TableToolbar
                ref={tableToolbarRef}
                modelName={modelName}
                numSelected={params.getSelected().size}
                filters={params.getFilters()}
                filtersInfo={filtersInfo}
                onCreate={onCreate}
                onChangeFilter={handleChangeFilter}
              />
            </>
          )
        }

        <TableContainer
          sx={height ? { maxHeight: height - (tableToolbarRef.current?.clientHeight || 0) - (tablePaginationRef.current?.clientHeight || 0) } : {}}
        >
          <MuiTable stickyHeader>
            <TableHead
              headCells={headCells}
              numSelected={params.getSelected().size}
              order={params.getOrder()}
              orderBy={params.getSort()}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={total_item_count}
              showSelect={showSelect}
            />
            <TableBody>
              {dataRows.map((row, index) => {
                const isItemSelected = params.getSelected().includes(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return renderRow({ isItemSelected, labelId, row, handleClick }, index);
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>

        <TablePagination
          ref={tablePaginationRef}
          rowsPerPageOptions={[items_per_page]}
          component="div"
          count={total_item_count}
          rowsPerPage={rowsPerPage}
          page={current_page - 1}
          onPageChange={handleChangePage}
          className={styles.pagination}
          classes={{
            root: styles.root,
            select: styles.select,
            spacer: styles.spacer,
            toolbar: styles.pagiToolbar,
          }}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Box>
  );
}

ResponsiveTable.propTypes = {
  ref: PropTypes.object,
  height: PropTypes.number,
  modelName: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  info: PropTypes.object.isRequired,
  filtersInfo: PropTypes.object,
  headCells: PropTypes.array.isRequired,
  hideToolbar: PropTypes.bool,
  multiple: PropTypes.bool,
  renderRow: PropTypes.func.isRequired,
  renderMobileCard: PropTypes.func,
  onCreate: PropTypes.func,
  onClickView: PropTypes.func,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onClickUnsubscribe: PropTypes.func,
  showSelect: PropTypes.bool,
};

export default ResponsiveTable;
